/**
 * 🛡️ AdminInvoices — Dashboard admin facturation B2C (AMSY E-COM / COACH DIA)
 *
 * Métriques agrégées + déclenchement manuel du cron de rétention 10 ans
 * (archivage logique, zéro suppression physique).
 *
 * GET  /api/admin/invoices/metrics
 * POST /api/admin/invoices/run-retention
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowLeft, FileText, RefreshCw, Archive, Mail, Download,
  TrendingUp, AlertTriangle, Calendar, Euro, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/environment';

const PAYMENT_TYPE_LABELS = {
  subscription_monthly: 'Abonnement mensuel',
  one_shot: 'Achat unique',
  coaching_session: 'Coaching humain',
  recharge: 'Recharge sessions',
  unknown: 'Inconnu',
};

const fmtMoney = (n) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

const fmtInt = (n) =>
  new Intl.NumberFormat('fr-FR').format(Number(n || 0));

function StatCard({ icon: Icon, label, value, sub, accent = '#D4AF37', testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl bg-white border border-[#E8E2D2] p-4 sm:p-5"
      style={{ boxShadow: `0 1px 0 ${accent}11 inset` }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}1A`, color: accent }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#6B7280] font-semibold">
          {label}
        </span>
      </div>
      <div className="text-2xl sm:text-3xl font-light text-[#2A2319] tabular-nums">
        {value}
      </div>
      {sub && (
        <div className="mt-1 text-xs text-[#6B7280]">{sub}</div>
      )}
    </div>
  );
}

export default function AdminInvoices() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useAuth();
  const API = getApiUrl();
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/admin/invoices/metrics`, { headers });
      if (!r.ok) {
        throw new Error(`HTTP ${r.status}`);
      }
      const data = await r.json();
      setMetrics(data);
    } catch (e) {
      setError(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [API, token]); // eslint-disable-line

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const runRetention = async () => {
    if (running) return;
    setRunning(true);
    try {
      const r = await fetch(
        `${API}/admin/invoices/run-retention`,
        { method: 'POST', headers }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success(
        `🗄️ Rétention exécutée : ${data.archived} facture(s) archivée(s) (${data.scanned} examinée(s))`
      );
      await fetchMetrics();
    } catch (e) {
      toast.error(`Échec rétention : ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const T = metrics?.totals || {};
  const R = metrics?.retention || {};
  const E = metrics?.emails || {};
  const D = metrics?.downloads || {};
  const A = metrics?.activity || {};

  return (
    <div
      data-testid="admin-invoices-page"
      className="min-h-screen bg-[#FBF9F3] pt-16 sm:pt-8 pb-32 px-4"
    >
      <div className="max-w-6xl mx-auto">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 text-xs text-[#6B7280] hover:text-[#374151] mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Retour Admin
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="uppercase text-xs tracking-[0.2em] text-[#92710A] mb-1">
              Administration · AMSY E-COM
            </p>
            <h1 className="text-xl sm:text-2xl font-light text-[#2A2319]">
              Facturation B2C
            </h1>
            <p className="text-xs text-[#6B7280] mt-1">
              Pilotage financier — émission, rétention 10 ans, distribution
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchMetrics}
              disabled={loading}
              data-testid="admin-invoices-refresh-btn"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#141415] border border-[#E8E2D2] text-[#374151] hover:border-[#D4AF37]/40 text-xs"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
              />
              Actualiser
            </button>
            <button
              onClick={runRetention}
              disabled={running || loading}
              data-testid="admin-invoices-run-retention-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#B7901F] to-[#D4AF37] text-[#0A0A0B] font-semibold text-xs hover:shadow-lg hover:shadow-[#D4AF37]/20 transition"
            >
              <Archive className="w-3.5 h-3.5" />
              {running ? 'Exécution…' : 'Lancer rétention'}
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Erreur : {error}
          </div>
        )}

        {loading && !metrics ? (
          <div data-testid="admin-invoices-loading" className="text-[#6B7280] text-sm py-12 text-center">
            Chargement des métriques…
          </div>
        ) : metrics ? (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <StatCard
                testid="kpi-count"
                icon={FileText}
                label="Factures émises"
                value={fmtInt(T.count)}
                sub={`${fmtInt(A.last_30_days)} sur 30 j`}
              />
              <StatCard
                testid="kpi-amount"
                icon={Euro}
                label="Montant TTC total"
                value={fmtMoney(T.amount_ttc)}
                sub={`HT ${fmtMoney(T.amount_ht)} · TVA ${fmtMoney(T.tax_amount)}`}
                accent="#48825A"
              />
              <StatCard
                testid="kpi-active"
                icon={ShieldCheck}
                label="Factures actives"
                value={fmtInt(R.active)}
                sub={`Rétention ${R.retention_years} ans`}
                accent="#7A4FCF"
              />
              <StatCard
                testid="kpi-archived"
                icon={Archive}
                label="Archivées (logique)"
                value={fmtInt(R.archived)}
                sub={`${fmtInt(R.expiring_within_90d)} expirent < 90 j`}
                accent="#9A8470"
              />
            </div>

            {/* Bloc 1 : ventilation par type */}
            <section className="rounded-xl bg-white border border-[#E8E2D2] p-5 mb-6">
              <h2 className="text-sm uppercase tracking-[0.18em] text-[#92710A] mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Répartition par type de paiement
              </h2>
              <div data-testid="metrics-by-payment-type" className="space-y-2">
                {(metrics.by_payment_type || []).length === 0 && (
                  <div className="text-[#6B7280] text-sm">Aucune facture émise.</div>
                )}
                {(metrics.by_payment_type || []).map((row) => {
                  const pct = T.amount_ttc
                    ? Math.round((row.amount_ttc / T.amount_ttc) * 100)
                    : 0;
                  return (
                    <motion.div
                      key={row.payment_type}
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-3 p-3 rounded-lg bg-[#FAF7EE] border border-[#F1ECDC]"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-[#2A2319] truncate">
                          {PAYMENT_TYPE_LABELS[row.payment_type] || row.payment_type}
                        </div>
                        <div className="text-xs text-[#6B7280]">
                          {fmtInt(row.count)} facture(s) · {pct}%
                        </div>
                        <div className="mt-1.5 h-1 bg-[#F1ECDC] rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-[#B7901F] to-[#D4AF37]"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right tabular-nums">
                        <div className="text-[#2A2319] font-semibold text-sm">
                          {fmtMoney(row.amount_ttc)}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Bloc 2 : Emails + Downloads */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <section className="rounded-xl bg-white border border-[#E8E2D2] p-5">
                <h2 className="text-sm uppercase tracking-[0.18em] text-[#92710A] mb-4 flex items-center gap-2">
                  <Mail className="w-4 h-4" /> Distribution Email
                </h2>
                <div data-testid="metrics-emails" className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#4B5563]">Envoyés avec succès</span>
                    <span className="text-emerald-700 tabular-nums">{fmtInt(E.sent)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#4B5563]">Jamais envoyés</span>
                    <span className="text-[#374151] tabular-nums">{fmtInt(E.never_sent)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#4B5563]">Événements d'échec (audit)</span>
                    <span className="text-red-700 tabular-nums">{fmtInt(E.failed_events)}</span>
                  </div>
                </div>
              </section>

              <section className="rounded-xl bg-white border border-[#E8E2D2] p-5">
                <h2 className="text-sm uppercase tracking-[0.18em] text-[#92710A] mb-4 flex items-center gap-2">
                  <Download className="w-4 h-4" /> Téléchargements PDF
                </h2>
                <div data-testid="metrics-downloads" className="space-y-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#4B5563]">Total téléchargements</span>
                    <span className="text-[#2A2319] tabular-nums">{fmtInt(D.total)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#4B5563]">Jamais téléchargées</span>
                    <span className="text-[#374151] tabular-nums">{fmtInt(D.never_downloaded)}</span>
                  </div>
                </div>
              </section>
            </div>

            {/* Bloc 3 : par année */}
            <section className="rounded-xl bg-white border border-[#E8E2D2] p-5 mb-6">
              <h2 className="text-sm uppercase tracking-[0.18em] text-[#92710A] mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Historique annuel
              </h2>
              <div data-testid="metrics-by-year" className="space-y-2">
                {(metrics.by_year || []).length === 0 && (
                  <div className="text-[#6B7280] text-sm">Aucune donnée.</div>
                )}
                {(metrics.by_year || []).map((row) => (
                  <div
                    key={row.year}
                    className="flex justify-between items-center p-3 rounded-lg bg-[#FAF7EE] border border-[#F1ECDC]"
                  >
                    <span className="text-[#2A2319] font-mono text-sm">{row.year}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-[#4B5563] text-xs">
                        {fmtInt(row.count)} facture(s)
                      </span>
                      <span className="text-[#2A2319] font-semibold text-sm tabular-nums">
                        {fmtMoney(row.amount_ttc)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="text-xs text-[#9CA3AF] text-center mt-8">
              📌 Conformité Code de commerce FR (art. L123-22) — conservation 10 ans · archivage logique uniquement · zéro suppression physique
              <br />
              Métriques générées le{' '}
              {metrics.generated_at
                ? new Date(metrics.generated_at).toLocaleString('fr-FR')
                : '—'}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
